/**
 * Determines if errors should be ignored based on Chrome version filtering
 * - For Chrome: Ignores errors from versions older than the last 10 versions
 * - For all other browsers: Never ignores errors (Firefox, Safari, Edge, etc.)
 *
 * This helps reduce noise from very old Chrome versions while still
 * capturing errors from other browsers regardless of version.
 *
 * @param chromeMinimumVersion - The minimum Chrome version to accept for error reporting
 * @returns true if the error should be ignored, false if it should be reported
 */
export const shouldIgnoreOutdatedChrome = (
  chromeMinimumVersion: number,
): boolean => {
  const { userAgent } = navigator;

  // Check if it's Opera (Chromium-based but should be treated as non-Chrome)
  if (userAgent.includes('OPR/')) {
    return false;
  }

  const chromeVersionMatch = userAgent.match(/Chrome\/([^.\s]*)/);

  // If not Chrome, never ignore errors from other browsers (Firefox, Safari, Edge, etc.)
  if (!chromeVersionMatch) {
    return false;
  }

  const versionString = chromeVersionMatch[1];
  const userChromeVersion = parseInt(versionString, 10);

  if (Number.isNaN(userChromeVersion)) {
    return true;
  }

  return userChromeVersion < chromeMinimumVersion;
};
